import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { brl } from "@/lib/api";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-6", "lg:col-span-6"];

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function ServiceCard({ service, onOrder, className = "", index = 0 }) {
  return (
    <Reveal delay={(index % 2) * 0.1} className={className}>
      <article className="card-frame group flex h-full flex-col overflow-hidden" data-testid={`service-card-${service.id}`}>
        <div className="overflow-hidden bg-paper3" style={{ aspectRatio: service.featured ? "16/9" : "3/2" }}>
          {service.image_url ? (
            <img
              src={service.image_url}
              alt={service.title}
              loading="lazy"
              className="service-img h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center font-display text-4xl text-ink-3">E</div>
          )}
        </div>
        <div className="flex flex-1 flex-col p-6 sm:p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3">{service.category}</p>
          <h3 className="mt-3 font-display text-xl font-bold tracking-tight sm:text-2xl">{service.title}</h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-2">{service.description}</p>
          <div className="mt-6 flex items-center justify-between border-t border-line pt-5">
            <div>
              <p className="font-display text-2xl font-extrabold tracking-tight">{brl(service.price)}</p>
              <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-3">
                <Clock className="h-3 w-3" /> {service.delivery_days} dias úteis
              </p>
            </div>
            <button
              data-testid={`service-card-order-button-${service.id}`}
              onClick={() => onOrder(service)}
              className="btn-lift flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper hover:bg-accent"
            >
              Encomendar <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Services({ services, onOrder }) {
  return (
    <section id="servicos" className="py-20 sm:py-32 lg:py-40" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-12 lg:px-20">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-3">01 — Serviços</p>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Escolha o que a sua marca precisa.
            </h2>
            <p className="max-w-sm text-sm leading-relaxed text-ink-2 sm:text-base">
              Tudo sob encomenda, com briefing individual e entregas entre 2 e 7 dias úteis. Valores de R$ 40 a R$ 200.
            </p>
          </div>
        </Reveal>

        {services === null && (
          <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12" data-testid="services-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`${SPANS[i]} h-80 animate-pulse rounded-2xl bg-paper2`} />
            ))}
          </div>
        )}

        {Array.isArray(services) && services.length === 0 && (
          <p className="mt-16 text-ink-2" data-testid="services-empty">
            Nenhum serviço disponível no momento — fale com o estúdio pelo Instagram @essenzaabr.
          </p>
        )}

        {Array.isArray(services) && services.length > 0 && (
          <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            {services.map((service, i) => (
              <ServiceCard
                key={service.id}
                service={service}
                index={i}
                onOrder={onOrder}
                className={SPANS[i % SPANS.length]}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
